import type {
	IShoppingItem,
	IVpcConfiguration,
	IVpcConfigurationImage,
	IVpcConfigurationSave
} from '@inter-ikea-kompis/types';

import DexfVpcService from '../../services/vpc/dexf/DexfVpcService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import type IVpcServiceOptions from './IVpcServiceOptions.js';

/**
 * This class is used as a simple interface for VPC services.
 */
export default class VpcService {
	private readonly applicationName: string;
	private readonly dexfApplicationId: string | null;
	private readonly dexfConfigurationVersion?: string;
	private readonly dexfIcfVersion?: string;
	private readonly dexfConfiguration: DexfConfiguration;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: IVpcServiceOptions) {
		this.applicationName = options.applicationName;
		this.dexfApplicationId = options.dexfApplicationId || null;
		this.dexfConfigurationVersion = options.dexfConfigurationVersion;
		this.dexfIcfVersion = options.dexfIcfVersion;
		this.dexfConfiguration = new DexfConfiguration(options);
	}

	/**
	 * Saves a planner configuration.
	 *
	 * @param configuration Configuration to save.
	 * @param shoppingItems Product items.
	 * @param [configurationImage] Configuration image as a base 64 string (e.g. "data:image/jpeg;base64,abc..").
	 * @param insightsCombinationFormat Insights Combination Format.
	 * @returns Promise object.
	 */
	public async saveConfiguration(
		configuration: Record<string, object | string | number | boolean>,
		shoppingItems: IShoppingItem[],
		configurationImage?: string | IVpcConfigurationImage[],
		insightsCombinationFormat?: Record<string, object | string | number | boolean>
	): Promise<IVpcConfigurationSave | null> {
		const service = await this.getService();
		return await service.saveConfiguration(
			configuration,
			shoppingItems,
			configurationImage,
			insightsCombinationFormat
		);
	}

	/**
	 * Fetches and returns a planner configuration.
	 *
	 * @param id Configuration ID (VPC Code).
	 * @param options ...
	 * @param options.includeIcfData Whether to include ICF data in the response or not.
	 * @returns Promise object.
	 */
	public async getConfiguration(
		id: string,
		options?: { includeIcfData: boolean }
	): Promise<IVpcConfiguration | null> {
		const service = await this.getService();
		return await service.getConfiguration(id, options);
	}

	/**
	 * Returns service.
	 *
	 * @returns Service.
	 */
	private async getService(): Promise<DexfVpcService> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const dexfApiKey = await this.dexfConfiguration.getDexfApiKeyOrThrow();

		return new DexfVpcService({
			endpoint: kompis.endpoints.dexfVpcService,
			dexfApplicationId: this.dexfApplicationId || this.applicationName,
			dexfApiKey,
			dexfConfigurationVersion: this.dexfConfigurationVersion,
			dexfIcfVersion: this.dexfIcfVersion
		});
	}
}
